import { Link, useLoaderData, redirect } from "react-router";
import { useState, useEffect } from "react";
import type { Route } from "./+types/blog.$slug";
import { MarketingNav, MarketingFooter } from "../components/layout";
import { NewsletterCapture } from "../components/marketing/NewsletterCapture";

export async function loader({ params }: Route.LoaderArgs) {
  const baseUrl =
    process.env.NODE_ENV === "production"
      ? "https://schoolconferencego.com"
      : `http://localhost:${process.env.API_PORT || 4002}`;

  try {
    const response = await fetch(`${baseUrl}/api/articles/${params.slug}`);
    if (!response.ok) {
      throw new Response("Article not found", { status: 404 });
    }
    const article = await response.json();

    // Duplicate articles are collapsed into one canonical. A redirected article
    // keeps a `redirectTo` slug; send its old URL to the keeper with a permanent
    // 301 so any link equity and bookmarks follow. (Must happen here at the page
    // route, not in the JSON API — a server-side fetch would silently follow an
    // API-level redirect and render the keeper at the old URL instead.)
    if (article?.redirectTo) {
      throw redirect(`/blog/${encodeURIComponent(article.redirectTo)}`, 301);
    }

    let seriesArticles: any[] = [];
    if (article.metadata?.series) {
      try {
        const seriesRes = await fetch(`${baseUrl}/api/articles/series/${article.metadata.series}`);
        if (seriesRes.ok) {
          const seriesData = await seriesRes.json();
          seriesArticles = seriesData.articles || [];
        }
      } catch {
        // Non-critical
      }
    }

    return { article, seriesArticles };
  } catch (e) {
    // Preserve intentional Responses (the 301 redirect above and the 404) —
    // a bare catch would turn our redirect into a 404.
    if (e instanceof Response) throw e;
    throw new Response("Article not found", { status: 404 });
  }
}

export function meta({ data, params }: Route.MetaArgs) {
  const siteUrl = "https://schoolconferencego.com";

  if (!data?.article) {
    return [
      { title: "Article Not Found | SchoolConferenceGo" },
      { property: "og:image", content: `${siteUrl}/og-image.jpg` },
    ];
  }

  const title = data.article.metaTitle || `${data.article.title} · Insights · SchoolConferenceGo`;
  const description = data.article.metaDescription || data.article.excerpt;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:type", content: "article" },
    { property: "og:url", content: `${siteUrl}/blog/${params.slug}` },
    { property: "og:title", content: data.article.title },
    { property: "og:description", content: description },
    { property: "og:image", content: `${siteUrl}/og-image.jpg` },
    { property: "og:site_name", content: "School Conference Go" },
    { property: "article:published_time", content: data.article.publishedAt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: data.article.title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: `${siteUrl}/og-image.jpg` },
    { rel: "canonical", href: `${siteUrl}/blog/${params.slug}` },
  ];
}

export default function BlogArticle() {
  const { article, seriesArticles } = useLoaderData<typeof loader>();
  const [sanitizedContent, setSanitizedContent] = useState(article.content || "");

  useEffect(() => {
    import("dompurify").then((DOMPurify) => {
      setSanitizedContent(DOMPurify.default.sanitize(article.content));
    });
  }, [article.content]);

  const isSeriesArticle = article.metadata?.series && seriesArticles.length > 0;
  const currentOrder = article.metadata?.seriesOrder || 0;
  const prevArticle = isSeriesArticle
    ? seriesArticles.find((a: any) => a.metadata?.seriesOrder === currentOrder - 1)
    : null;
  const nextArticle = isSeriesArticle
    ? seriesArticles.find((a: any) => a.metadata?.seriesOrder === currentOrder + 1)
    : null;

  const siteUrl =
    typeof window !== "undefined" && window.location.hostname === "localhost"
      ? "http://localhost:3002"
      : "https://schoolconferencego.com";

  return (
    <div className="min-h-[100dvh] bg-[var(--color-paper)] text-[var(--color-ink)]">
      <MarketingNav active="insights" />

      <div className="border-b border-[var(--color-hairline)] bg-white">
        <div className="mx-auto max-w-[720px] px-6 py-4">
          <nav className="scg-mono flex items-center gap-2 text-[0.75rem] text-[var(--color-ash)]">
            <Link to="/" className="no-underline hover:text-[var(--color-board)]">
              Home
            </Link>
            <span>/</span>
            <Link to="/insights" className="no-underline hover:text-[var(--color-board)]">
              Insights
            </Link>
            <span>/</span>
            <span className="truncate text-[var(--color-ink)]">{article.title}</span>
          </nav>
        </div>
      </div>

      <article className="mx-auto max-w-[720px] px-6 py-12">
        <header className="mb-10">
          <div className="mb-4 flex flex-wrap gap-2">
            {isSeriesArticle && (
              <span className="scg-mono rounded-full border border-[var(--color-confirm)] px-3 py-1 text-[0.68rem] tracking-[0.06em] text-[var(--color-confirm)]">
                Conference Season Prep · Part {currentOrder} of {seriesArticles.length}
              </span>
            )}
            {article.tags?.map((tag: string) => (
              <span
                key={tag}
                className="scg-mono rounded-full border border-[var(--color-hairline)] bg-[var(--color-panel)] px-3 py-1 text-[0.68rem] tracking-[0.06em] text-[var(--color-board)]"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="mb-4 text-[clamp(1.8rem,4vw,2.6rem)] font-extrabold leading-[1.15] tracking-[-0.02em] text-[var(--color-ink)]">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 scg-mono text-[0.78rem] text-[var(--color-ash)]">
            {article.targetAudience && (
              <span className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-[var(--color-board)]">
                {article.targetAudience}
              </span>
            )}
            {article.readingTime && <span>{article.readingTime}</span>}
            <time dateTime={article.publishedAt} suppressHydrationWarning>
              {new Date(article.publishedAt).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </time>
          </div>
        </header>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.excerpt,
              image: `${siteUrl}/og-image.jpg`,
              datePublished: article.publishedAt,
              dateModified: article.publishedAt,
              author: { "@type": "Organization", name: "School Conference Go" },
              publisher: {
                "@type": "Organization",
                name: "School Conference Go",
                logo: {
                  "@type": "ImageObject",
                  url: `${siteUrl}/school-conference-go.png@1000x.png`,
                },
              },
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `${siteUrl}/blog/${article.slug}`,
              },
            }),
          }}
        />

        <div
          className="prose prose-lg max-w-none prose-headings:font-extrabold prose-headings:text-[var(--color-ink)] prose-h2:mt-10 prose-h2:mb-4 prose-h2:text-2xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-xl prose-p:text-[var(--color-ink)] prose-p:leading-relaxed prose-a:text-[var(--accent-dark)] prose-a:font-semibold prose-a:no-underline hover:prose-a:underline prose-li:text-[var(--color-ink)] prose-strong:text-[var(--color-ink)] prose-blockquote:border-[var(--accent)] prose-blockquote:text-[var(--color-ash)]"
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />

        {isSeriesArticle && (
          <div className="mt-12 overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-hairline)] bg-white shadow-[var(--shadow-1)]">
            <div className="grid grid-cols-1 divide-y divide-[var(--color-hairline)] md:grid-cols-2 md:divide-x md:divide-y-0">
              {prevArticle ? (
                <Link to={`/blog/${prevArticle.slug}`} className="block p-5 no-underline hover:bg-[var(--color-panel)]">
                  <span className="scg-mono text-[0.68rem] uppercase text-[var(--color-ash)]">Previous</span>
                  <p className="mt-1 line-clamp-2 text-sm font-bold text-[var(--color-ink)]">{prevArticle.title}</p>
                </Link>
              ) : (
                <div className="p-5" />
              )}
              {nextArticle ? (
                <Link
                  to={`/blog/${nextArticle.slug}`}
                  className="block p-5 text-right no-underline hover:bg-[var(--color-panel)]"
                >
                  <span className="scg-mono text-[0.68rem] uppercase text-[var(--color-ash)]">Next</span>
                  <p className="mt-1 line-clamp-2 text-sm font-bold text-[var(--color-ink)]">{nextArticle.title}</p>
                </Link>
              ) : (
                <div className="p-5" />
              )}
            </div>

            <details className="border-t border-[var(--color-hairline)]">
              <summary className="cursor-pointer px-5 py-3 text-sm font-bold text-[var(--color-board)] hover:bg-[var(--color-panel)]">
                View all {seriesArticles.length} articles in this series
              </summary>
              <ol className="space-y-2 px-5 pb-4">
                {seriesArticles.map((sa: any) => (
                  <li key={sa._id}>
                    {sa.slug === article.slug ? (
                      <span className="flex items-center gap-2 text-sm font-medium text-[var(--color-ink)]">
                        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-board)] scg-mono text-xs text-[var(--color-paper)]">
                          {sa.metadata?.seriesOrder}
                        </span>
                        {sa.title}
                        <span className="scg-mono text-[0.68rem] text-[var(--color-ash)]">(you are here)</span>
                      </span>
                    ) : (
                      <Link
                        to={`/blog/${sa.slug}`}
                        className="flex items-center gap-2 text-sm text-[var(--color-ash)] no-underline hover:text-[var(--color-board)]"
                      >
                        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-panel)] scg-mono text-xs text-[var(--color-board)]">
                          {sa.metadata?.seriesOrder}
                        </span>
                        {sa.title}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </details>
          </div>
        )}

        <div className="mt-12 rounded-[var(--radius-md)] border border-[var(--color-hairline)] bg-[var(--color-panel)] p-6">
          <NewsletterCapture
            sourceComponent="blog-inline"
            variant="inline"
          />
        </div>

        <div className="mt-12 rounded-[var(--radius-lg)] bg-[var(--color-board)] p-8 text-center text-[var(--color-paper)]">
          <h2 className="mb-3 text-[1.5rem] font-extrabold text-[var(--color-paper)]">
            Ready to simplify your conferences?
          </h2>
          <p className="mx-auto mb-6 max-w-lg text-[rgba(247,245,240,0.8)]">
            Build the real event, preview what families see, and pay only when you publish.
          </p>
          <Link to="/register" className="btn btn-publish no-underline">
            Start building free
          </Link>
        </div>

        <div className="mt-10 border-t border-[var(--color-hairline)] pt-8">
          <Link to="/insights" className="font-bold text-[var(--color-board)] no-underline hover:underline">
            ← Back to all Insights
          </Link>
        </div>
      </article>

      <MarketingFooter />
    </div>
  );
}
